import { createContext, useContext, useState, useEffect } from "react";
import queryString from "query-string";
import html2canvas from "html2canvas";
import BaseAPI from "../utils/Base.API";

import handleEvents from "../utils/card/handleEvents";

const cardContextDefaultValues = {
  staffDetail: {},
  templates: [],
  texts: [],
  images: [],
  activeTemplate: null,
  backgroundColor: "#ffffff",
  backgroundImage: "",
  activeId: null,
  loading: null,
  handleEvents: {},
  storeStaffData: () => {},
  storeTemplates: () => {},
  storeTexts: () => {},
  storeImages: () => {},
  storeActiveTemplate: () => {},
  storeBackgroundColor: () => {},
  storeBackgroundImage: () => {},
  storeActiveId: () => {},
  storeLoading: () => {},
  getImageURLFromCanvas: async () => {},
  uploadImageAndSendEmail: async () => {},
  modalState: {
    isOpen: false,
    modalKey: 0,
    modalType: "success",
    modalHeader: "",
    modalText: "",
  },
  openModal: () => {},
  closeModal: () => {},
  getPickerPosition: () => {},
};

const CardContext = createContext(cardContextDefaultValues);

export function CardProvider({ children }) {
  const query = queryString.parse(window.location.search);
  let userData = window.$user_profile;
  const [staffDetail, setStaffDetail] = useState({});
  const [templates, setTemplates] = useState([]);
  const [texts, setTexts] = useState([]);
  const [images, setImages] = useState([]);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [backgroundColor, setBackgroundColor] = useState("");
  const [backgroundImage, setBackgroundImage] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalState, setModalState] = useState({
    isOpen: false,
    modalKey: 0,
    modalType: "success",
    modalHeader: "",
    modalText: "",
  });

  useEffect(() => {
    const fetchStaffDetail = async () => {
      setLoading(true);
      try {
        const response = await BaseAPI.get(`birthday/api/user`, {
          params: {
            email: query.email,
          },
        });
        const decrypt = response?.data;
        const data = decrypt?.data ?? {};
        // console.log("staffDetail=", data);
        if (data?.name) {
          setStaffDetail({ ...data, createdBy: userData.emailadd });
        }
      } catch (error) {
        setLoading(false);
        console.error("Failed to fetch templates:", error);
      }
    };

    if (userData?.idu && query?.email && !staffDetail?.email) {
      fetchStaffDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query?.email]);

  useEffect(() => {
    const fetchTemplates = async () => {
      setLoading(true);
      try {
        const response = await BaseAPI.get("birthday/api/template");
        const payload = response?.data ?? {};
        const fetchedTemplates =
          payload?.data?.templates ?? payload?.templates ?? [];
        if (fetchedTemplates.length > 0) {
          setTemplates(fetchedTemplates);

          // Find the active template (default = 1 or fallback to the first template)
          const firstTemplate =
            fetchedTemplates.find((template) => template.default === 1) ||
            fetchedTemplates[0];

          setActiveTemplate(firstTemplate);
          storeTextsAfterTemplateChanged(
            firstTemplate?.texts ?? [],
            staffDetail,
          );
          storeImagesAfterTemplateChanged(
            firstTemplate?.images ?? [],
            staffDetail,
          );

          // Set background based on the active template
          setBackgroundColor(firstTemplate.backgroundColor || "#ffffff");
          setBackgroundImage(firstTemplate.backgroundImage || "");
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.error("Failed to fetch templates:", error);
      }
    };

    if (userData?.idu && staffDetail?.email && templates.length === 0) {
      fetchTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffDetail?.email]);

  // useEffect(() => {
  //   // Simulate a delay for the preloader (adjust time as needed)
  //   const timer = setTimeout(() => {
  //     setLoading(false);
  //   }, 2000); // 2 seconds

  //   return () => clearTimeout(timer);
  // }, []);

  useEffect(() => {
    handleEvents.setContextFunctions({
      staffDetail,
      storeStaffDetail,
      storeTemplates,
      storeTexts,
      storeImages,
      storeTextsAfterTemplateChanged,
      storeImagesAfterTemplateChanged,
      storeActiveTemplate,
      storeBackgroundColor,
      storeBackgroundImage,
      storeActiveId,
      getImageURLFromCanvas,
      uploadImageAndSendEmail,
      modalState,
      openModal,
      closeModal,
      storeLoading,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffDetail, templates, texts, images]);

  const storeStaffDetail = (data) => {
    setStaffDetail(data);
  };

  const storeTemplates = (data) => {
    setTemplates(data);
  };

  const storeTexts = (data) => {
    setTexts(data);
  };

  const storeImages = (data) => {
    setImages(data);
  };

  const storeTextsAfterTemplateChanged = (matchingTexts, staffDetail) => {
    setTexts(
      matchingTexts.length > 0
        ? matchingTexts.map((text) => {
            let content = text.content.replace(
              "{staff-name}",
              staffDetail.name,
            );
            content = content.replace("{staff-birthday}", staffDetail.birthday);
            content = content.replace(
              "{staff-createdby}",
              staffDetail.createdBy,
            );
            return {
              id: text.id,
              type: text.type || "text",
              content: content,
              position: text.position || { x: 50, y: 50 },
              width: text.width || 200,
              height: text.height || 28,
              fontSize: text.fontSize || 16,
              color: text.color || "#000000",
              fontFamily: text.fontFamily || "Arial",
              fontWeight: text.fontWeight || "normal",
              backgroundColor: text.backgroundColor || "none",
            };
          })
        : [], // If no matching texts, set empty array
    );
  };

  const storeImagesAfterTemplateChanged = (matchingImages, staffDetail) => {
    setImages(
      matchingImages.length > 0
        ? matchingImages.map((image) => {
            let url = image.src.replace(
              "{staff-image-url}",
              staffDetail.imageURL,
            );
            return {
              id: image.id,
              src: url,
              position: image.position || { x: 150, y: 150 },
              width: image.width || 150,
              height: image.height || 150,
              rotation: image.rotation || 0,
              shape: image.shape || "square",
            };
          })
        : [], // If no matching texts, set empty array
    );
  };

  const storeActiveTemplate = (data) => {
    setActiveTemplate(data);
  };

  const storeBackgroundColor = (data) => {
    setBackgroundColor(data);
  };

  const storeBackgroundImage = (data) => {
    setBackgroundImage(data);
  };

  const storeActiveId = (data) => {
    setActiveId(data);
  };

  const storeLoading = (data) => {
    setLoading(data);
  };

  const getImageURLFromCanvas = async (elementId) => {
    const element = document.getElementById(elementId);

    if (!element) {
      console.error(`Element with ID "${elementId}" not found!`);
      return { blob: null, imageURL: null };
    }

    // Store the original overflow style before modifying
    let originalOverflow = element.style.overflow;

    try {
      await loadImages(element); // Ensure images are fully loaded

      element.style.setProperty("overflow", "hidden", "important");

      // Wait for a short time to ensure the style is applied
      await new Promise((resolve) => setTimeout(resolve, 100));

      const canvas = await html2canvas(element, {
        useCORS: true,
        backgroundColor: null,
        scale: 1.2,
        width: element.scrollWidth,
        height: element.scrollHeight,
      });

      // Restore the original overflow style
      // element.style.overflow = originalOverflow;
      element.style.setProperty("overflow", originalOverflow, "important");

      return new Promise((resolve) => {
        canvas.toBlob((blob) => {
          if (!blob) {
            console.error("Failed to generate image blob.");
            resolve({ blob: null, imageURL: null });
            return;
          }

          const imageURL = URL.createObjectURL(blob);
          resolve({ blob, imageURL });
        }, "image/png");
      });
    } catch (error) {
      console.error("Error capturing element as image:", error);
      element.style.setProperty("overflow", originalOverflow, "important");
      return { blob: null, imageURL: null };
    }
  };

  const loadImages = async (element) => {
    const images = element.getElementsByTagName("img");
    const promises = Array.from(images).map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete) resolve();
          else img.onload = resolve;
        }),
    );
    await Promise.all(promises);
  };

  const uploadImageAndSendEmail = async (blob) => {
    if (!blob) {
      console.error("Invalid Blob for upload.");
      return null;
    }

    try {
      const yearMonth = new Date().toISOString().slice(0, 7).replace("-", ""); // YYYYMM
      const uploadDir = `photo/birthday/upload_card/${yearMonth}/`;
      // console.log(staffDetail, userData);
      // return;
      const formData = new FormData();
      formData.append("file", blob, "card.png");
      formData.append("uploadDir", uploadDir);
      formData.append("sendToName", staffDetail.name);
      formData.append("sendToEmail", staffDetail.email);
      formData.append("sendFromEmail", userData.emailadd);

      const result = await BaseAPI.post(
        "birthday/api/upload/sendemail",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return result;
    } catch (error) {
      console.error("Error uploading image:", error);
      return null;
    }
  };

  // ✅ Open Modal with type and custom text
  const openModal = (
    type = "success",
    header = "DIALOG",
    text = "Default message",
  ) => {
    console.log(`Opening Modal: ${type}, Text: ${text}`);
    setModalState((prev) => ({
      ...prev,
      isOpen: true,
      modalKey: prev.modalKey + 1,
      modalType: type,
      modalHeader: header,
      modalText: text, // ✅ Set custom text
    }));
    document.body.style.overflow = "hidden";
  };

  // ✅ Close Modal
  const closeModal = () => {
    console.log("Closing Modal");
    setModalState((prev) => ({
      ...prev,
      isOpen: false,
      modalHeader: "",
      modalText: "", // ✅ Clear text on close
    }));
    document.body.style.overflow = "auto";
  };

  const getPickerPosition = (pickerRef, elementId) => {
    const position = { top: 40, left: 0 };
    if (!pickerRef.current) return position;

    const icon = document.getElementById(elementId);
    if (!icon) return position;

    const iconRect = icon.getBoundingClientRect();
    const pickerRect = pickerRef.current.getBoundingClientRect();
    let lastRight = iconRect.left + pickerRect.width + 10;
    let lastTop = iconRect.bottom + pickerRect.height + 20;

    if (lastRight <= window.innerWidth && lastTop <= window.innerHeight) {
      return position;
    }
    // Check if it overflows on the right
    if (lastRight > window.innerWidth) {
      position.left = window.innerWidth - lastRight - 20;
    }

    // Check if it overflows on the bottom
    if (lastTop > window.innerHeight) {
      position.top = -pickerRect.height - 15; // Shift up
    }

    return position;
  };

  const value = {
    staffDetail,
    templates,
    texts,
    images,
    activeTemplate,
    backgroundColor,
    backgroundImage,
    activeId,
    loading,
    handleEvents,
    storeStaffDetail,
    storeTemplates,
    storeTexts,
    storeImages,
    storeTextsAfterTemplateChanged,
    storeImagesAfterTemplateChanged,
    storeActiveTemplate,
    storeBackgroundColor,
    storeBackgroundImage,
    storeActiveId,
    storeLoading,
    getImageURLFromCanvas,
    uploadImageAndSendEmail,
    modalState,
    openModal,
    closeModal,
    getPickerPosition,
  };

  return <CardContext.Provider value={value}>{children}</CardContext.Provider>;
}

export const useCard = () => useContext(CardContext);
